import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, AlertTriangle } from "lucide-react";
import { deleteHorse, getDeletedHorses, restoreHorse, purgeCheck, purgeHorse, STAGES } from "@/lib/pipelineApi";
import { formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ConfirmAction } from "@/components/contacts/ConfirmAction";
import { TestBadge } from "@/components/TestMode";
import { CardPhoto } from "@/components/pipeline/PipelineCard";

const stageLabel = (s) => (STAGES.find(([k]) => k === s) || [])[1] || "—";
const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "object" ? d.message : formatApiError(d) || "Something went wrong"; };

export const LiveBadge = () => <span className="inline-flex items-center rounded bg-emerald-700 text-white text-[10px] font-extrabold tracking-wider px-1.5 py-0.5">LIVE</span>;
const ModeBadge = ({ c }) => (c.is_test ? <TestBadge /> : <LiveBadge />);

// Soft-delete confirmation; published/scheduled ads are unpublished first after an explicit warning.
export function DeleteHorseDialog({ c, onClose, onDone }) {
  const ad = c.listing_status;
  const hasAd = ad === "published" || ad === "scheduled";
  return (
    <ConfirmAction open onClose={onClose} testId="delete-horse-dialog" danger confirmLabel={hasAd ? "Unpublish and delete" : "Delete horse"}
      title={<span className="flex items-center gap-2"><ModeBadge c={c} />Delete {c.name}?</span>}
      body={<>
        <p>The horse is removed from the active Pipeline and moved to <strong>Deleted Horses</strong>, where you can restore it to its stage.</p>
        {hasAd && <p data-testid="delete-horse-ad-warning" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-2 font-semibold">
          {ad === "published" ? "This horse has a published ad on the website. It will be unpublished first." : "This horse has an ad scheduled to publish. The schedule will be cancelled first."}</p>}
        <p>Signed contracts, sale records, history and the seller's contact account are kept.</p>
      </>}
      onConfirm={({ reason }) => deleteHorse({ key: c.key, unpublish: hasAd, reason })
        .then(() => { toast.success(`${c.name} moved to Deleted Horses`); onDone(); })
        .catch((e) => { toast.error(errMsg(e)); throw e; })} />
  );
}

function PurgeDialog({ c, onClose, onDone }) {
  const [chk, setChk] = useState(null);
  useEffect(() => { purgeCheck(c.key).then(setChk).catch((e) => { toast.error(errMsg(e)); onClose(); }); }, [c.key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!chk) return null;
  if (!chk.eligible) {
    return (
      <ConfirmAction open onClose={onClose} testId="purge-horse-blocked" title="This horse can't be permanently deleted" confirmLabel="Close"
        body={<p data-testid="purge-horse-reason">{chk.reason}</p>} onConfirm={async () => {}} noReason />
    );
  }
  return (
    <ConfirmAction open onClose={onClose} testId="purge-horse-dialog" danger confirmLabel="Permanently delete" typeToConfirm="DELETE" caseSensitive
      title={<span className="flex items-center gap-2"><ModeBadge c={c} />Permanently delete {c.name}?</span>}
      body={<>
        <p className="font-semibold text-red-700">This can't be undone.</p>
        <p className="font-semibold mt-1">Removed:</p><ul className="list-disc pl-5">{chk.removed.map((t) => <li key={t}>{t}</li>)}</ul>
        <p className="font-semibold mt-1">Kept:</p><ul className="list-disc pl-5">{chk.retained.map((t) => <li key={t}>{t}</li>)}</ul>
      </>}
      onConfirm={() => purgeHorse(c.key).then(() => { toast.success("Permanently deleted"); onDone(); }).catch((e) => { toast.error(errMsg(e)); throw e; })} />
  );
}

export function DeletedHorses({ mode, isSuper, onRestored }) {
  const [rows, setRows] = useState(null);
  const [purge, setPurge] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => getDeletedHorses(mode).then((d) => setRows(d.cards)).catch(() => toast.error("Could not load deleted horses"));
  useEffect(() => { setRows(null); load(); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = (c) => { setBusy(c.key); restoreHorse(c.key).then((r) => { toast.success(`${c.name} restored to ${stageLabel(r.stage)}`); load(); onRestored(); })
    .catch((e) => toast.error(errMsg(e))).finally(() => setBusy(null)); };
  if (!rows) return <p className="px-4 lg:px-8 py-6 text-sm text-stone-600"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading deleted horses…</p>;
  return (
    <main data-testid="deleted-horses" className="px-3 lg:px-8 py-4">
      {!rows.length && <p className="text-sm text-stone-600 text-center py-10">No deleted horses.</p>}
      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden divide-y divide-stone-100">
        {rows.map((c, i) => (
          <div key={c.key} data-testid={`deleted-horse-${i}`} className="flex flex-col sm:flex-row sm:items-center gap-3 px-3 py-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <CardPhoto c={c} className="h-14 w-16 shrink-0 rounded-lg" />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 min-w-0"><ModeBadge c={c} /><span className="font-semibold text-sm truncate">{c.name}</span></div>
                <div className="text-[11px] text-stone-600 truncate">{[c.details, c.owner].filter(Boolean).join(" · ")}</div>
                <div className="text-[11px] text-stone-500">
                  {c.deleted.archived_only ? "Archived" : "Deleted"} {c.deleted.at ? new Date(c.deleted.at).toLocaleDateString() : ""}{c.deleted.by ? ` by ${c.deleted.by}` : ""}
                  {c.deleted.stage ? ` · was in ${stageLabel(c.deleted.stage)}` : ""}{c.deleted.ad_unpublished ? " · ad unpublished" : ""}
                </div>
              </div>
            </div>
            <div className="flex gap-2 sm:shrink-0">
              <Button data-testid={`deleted-horse-restore-${i}`} variant="outline" disabled={busy === c.key} onClick={() => restore(c)} className="h-10 rounded-full flex-1 sm:flex-none">
                {busy === c.key ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}Restore</Button>
              {isSuper && <Button data-testid={`deleted-horse-purge-${i}`} variant="outline" onClick={() => setPurge(c)} className="h-10 rounded-full flex-1 sm:flex-none text-red-700 border-red-200 hover:bg-red-50">
                <AlertTriangle className="h-4 w-4 mr-1" />Permanently delete</Button>}
            </div>
          </div>
        ))}
      </div>
      {purge && <PurgeDialog c={purge} onClose={() => setPurge(null)} onDone={() => { setPurge(null); load(); }} />}
    </main>
  );
}
